import type { CompData } from '@wix/thunderbolt-becky-root'
import type { ColorMap, StyleProperties } from '@wix/thunderbolt-becky-types'
import { Design, Properties, VectorImageCSS } from './VectorImage'
import { assign, defaults, kebabCase, mapKeys, pickBy, isNumber, isString } from 'lodash'
import { FlipProperty } from '@wix/thunderbolt-components-native'
import { vectorImage, color } from '@wix/thunderbolt-commons'
import type { MaskFlip, ParsedSvg, SvgInfo } from '@wix/thunderbolt-commons'

const SVG_TYPES = {
	SHAPE: 'shape',
	TINT: 'tint',
	COLOR: 'color',
	UGC: 'ugc',
}

const ART_STYLE_DEFAULTS = {
	opacity: 1,
}

const DEFAULT_COLOR = '#242323'

const SHAPE_STYLE_DEFAULTS = {
	strokeWidth: 4,
	opacity: 1,
	stroke: '#5E97FF',
	strokeOpacity: 1,
	enableStroke: false,
}

const SKIN_STYLE_KEYS_MAP: Record<string, string> = {
	'alpha-fillcolor': 'opacity',
	'alpha-stroke': 'strokeOpacity',
	enablestroke: 'enableStroke',
	fillcolor: 'fill',
	stroke: 'stroke',
	strokewidth: 'strokeWidth',
	opacity: 'opacity',
}

export const getSvgType = (svgInfo: any) => svgInfo?.svgType || SVG_TYPES.SHAPE

function getSkinStyle(styleProps: StyleProperties) {
	return mapKeys(styleProps, (_, key) => SKIN_STYLE_KEYS_MAP[key])
}

const getVectorImageOpacity = (skinStyle: StyleProperties, shapeStyle: Design['shapeStyle']) => {
	const anyOpacity = [skinStyle?.opacity, shapeStyle?.opacity, ART_STYLE_DEFAULTS.opacity]
	return anyOpacity.find((val) => isNumber(val))
}

const transformStrokeWidth = (style: Design['shapeStyle']) => {
	const enableStroke = !!style?.enableStroke
	const strokeWidth = enableStroke ? style?.strokeWidth || 1 : 0
	return assign(
		pickBy(style, (_, key) => key !== 'enableStroke'),
		{ strokeWidth }
	) as Omit<Design['shapeStyle'], 'enableStroke'>
}

const flipTransformStyles: Record<string, object> = {
	x: { transform: 'scale(1, -1)' },
	y: { transform: 'scale(-1, 1)' },
	xy: { transform: 'scale(-1, -1)' },
	vertical: { transform: 'scale(1, -1)' },
	horizontal: { transform: 'scale(-1, 1)' },
	both: { transform: 'scale(-1, -1)' },
}

export const getSvgStringForMask = ({
	svgInfo,
	svgString,
	flip,
}: {
	svgInfo: SvgInfo
	svgString: string
	flip: MaskFlip
}) => {
	return vectorImage.getSvgContentForMask(svgString, svgInfo, flip)
}

export type CompDataVectorImage = CompData & { resolvedSvg?: ParsedSvg }

export const getVectorImageCSS = ({
	design,
	resolvedSvg,
	compProps,
	styleProperties,
	siteColors,
}: {
	design: Design
	resolvedSvg?: ParsedSvg
	compProps?: Properties
	styleProperties: StyleProperties
	siteColors: ColorMap
}) => {
	const svgType = getSvgType(resolvedSvg?.info)
	const skinStyle = getSkinStyle(styleProperties)
	const shapeStyle = design?.shapeStyle || {}

	const getShapeStyle = () => {
		const baseStyle = assign(shapeStyle, { fill: design?.overrideColors?.color1 || DEFAULT_COLOR })
		const style = defaults(skinStyle, baseStyle, SHAPE_STYLE_DEFAULTS)

		const transformedStyle = Object.entries(transformStrokeWidth(style)).reduce((result, [key, value]) => {
			if (isString(value) && (key === 'fill' || key === 'stroke')) {
				result[key] = color.getFromColorMap(value as string, siteColors)
			} else if (key === 'opacity') {
				result.fillOpacity = value
			} else {
				result[key] = value
			}

			return result
		}, {} as Record<string, string | number>)

		const strokeWidth = transformedStyle?.strokeWidth || 0
		const cssStrokeAdjustments = vectorImage.getCSSStrokeAdjustments(parseInt(strokeWidth as string, 10))
		return assign(transformedStyle, cssStrokeAdjustments)
	}

	const getTintShapeStyle = () => {
		const fill = skinStyle?.fill || design?.overrideColors?.color1 || DEFAULT_COLOR
		const resolvedFill = color.getFromColorMap(fill, siteColors)
		return { fill: resolvedFill }
	}

	const processedShapeStyle =
		(svgType === SVG_TYPES.SHAPE && getShapeStyle()) || (svgType === SVG_TYPES.TINT && getTintShapeStyle()) || {}

	const flip: FlipProperty | undefined = compProps?.flip
	const flipTransformStyle = flipTransformStyles[flip as string] || {}

	const opacityStyle =
		svgType === SVG_TYPES.SHAPE ? {} : { '--opacity': getVectorImageOpacity(skinStyle, shapeStyle) }

	const result = assign(processedShapeStyle, flipTransformStyle, opacityStyle)
	return (mapKeys(result, (_, key) => kebabCase(key)) as unknown) as VectorImageCSS
}
